/**
 * 客户端配置同步
 * 接收服务端下发的配置变更广播
 */
import type { Socket } from 'socket.io-client'
import { EventEmitter } from 'events'

export interface ConfigSyncOptions {
  /** 事件名，默认 config:update */
  event?: string
  /** 是否自动合并，默认 true */
  autoMerge?: boolean
  /** 配置版本追踪 */
  trackVersion?: boolean
}

export class ConfigSync extends EventEmitter {
  private socket: Socket | null = null
  private config: Record<string, any> = {}
  private version = 0
  private options: Required<ConfigSyncOptions>
  private history: Array<{ version: number; changes: Record<string, any>; timestamp: number }> = []

  constructor(initialConfig?: Record<string, any>, options?: ConfigSyncOptions) {
    super()
    this.config = initialConfig ? { ...initialConfig } : {}
    this.options = {
      event: options?.event ?? 'config:update',
      autoMerge: options?.autoMerge ?? true,
      trackVersion: options?.trackVersion ?? true,
    }
  }

  /** 绑定 socket 接收配置更新 */
  bind(socket: Socket): void {
    this.socket = socket
    socket.on(this.options.event, (data: { config: Record<string, any>; version?: number }) => {
      if (this.options.trackVersion && data.version !== undefined) {
        if (data.version <= this.version) return
        this.version = data.version
      }
      if (this.options.autoMerge) this.merge(data.config)
      this.emit('updated', data.config)
    })
  }

  /** 解绑 */
  unbind(): void {
    if (this.socket) {
      this.socket.off(this.options.event)
      this.socket = null
    }
  }

  /** 合并配置 */
  merge(changes: Record<string, any>): void {
    this.history.push({ version: this.version, changes: { ...changes }, timestamp: Date.now() })
    Object.assign(this.config, changes)
  }

  /** 获取配置 */
  get<T = any>(key: string): T | undefined {
    return this.config[key] as T
  }

  /** 获取全部配置 */
  getAll(): Record<string, any> {
    return { ...this.config }
  }

  /** 获取版本 */
  getVersion(): number {
    return this.version
  }

  /** 获取变更历史 */
  getHistory(): typeof this.history {
    return [...this.history]
  }

  /** 重置配置 */
  reset(config?: Record<string, any>): void {
    this.config = config ? { ...config } : {}
    this.version = 0
    this.history = []
  }
}
